import React, { useState, useMemo, useEffect } from "react";
import cn from "classnames";

// component
import { Icon } from "@trussworks/react-uswds";
import {
  Dialog,
  DialogContent,
  DialogHeading,
  DialogClose,
  IconButton,
  Button,
  ComboBox,
  MultiSelectCheckbox,
  Checkbox,
  SearchInput,
} from "components/LibraryComponents/Components";
import MSAAccordion from "./MSAAccordion";

// hooks
import useDebounce from "hooks/useDebounce";

// utils
import { getHighlightedText } from "utils/advanced-search";

// styles
import styles from "./MSADialog.module.scss";

// query
import useMSACodes, { GroupedMSAFilterTerm, MSAFilterTerm } from "./query";
import { Generic } from "types/filters-store.types";

// store
import useFiltersStore from "store/useFiltersStoreNew";
import { FilterKeys } from "constants/filters";

type SelectedMSA = { checked: boolean } & Generic;

type MSADialogProps = {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export default function MSADialog(props: MSADialogProps) {
  const { location, setFilter } = useFiltersStore((state) => ({
    location: state.filters.location,
    setFilter: state.setFilter,
  }));

  const [searchTerm, setSearchTerm] = useState<string>("");
  const [openAccordoing, setOpenAccordoing] = useState<string>("");
  const [selectedItems, setSelectedItems] = useState<SelectedMSA[]>([]);

  const { msaCodes } = useMSACodes();

  const groupedMSA = groupedMSAByState(msaCodes);

  const debouncedSearchTerm = useDebounce(searchTerm, 150);

  const isSearchState = searchTerm.length > 0;
  const selecttedMSAValues = selectedItems
    .filter((item) => item.checked)
    .map((item) => item.value);
  const isDisabled = selecttedMSAValues.length === 0;

  const filteredMSACodes = useMemo(() => {
    if (!debouncedSearchTerm.trim()) return [];

    // Filter the list based on the search term and limit the results
    const searchLower = debouncedSearchTerm.toLowerCase();
    const filtered = msaCodes.filter(
      (item) =>
        item.value.toLowerCase().startsWith(searchLower) ||
        item.label.toLowerCase().includes(searchLower)
    );

    const highlightedList = getHighlightedText(
      filtered,
      debouncedSearchTerm,
      true
    );

    return highlightedList as MSAFilterTerm[];
  }, [msaCodes, debouncedSearchTerm]);

  useEffect(() => {
    if (location.msas.length > 0) {
      setSelectedItems(
        location.msas.map((item) => ({ ...item, checked: true }))
      );
    }
  }, [location.msas]);

  const handleAccordionOpen = (state: string) => {
    if (openAccordoing === state) {
      setOpenAccordoing("");
    } else {
      setOpenAccordoing(state);
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchTerm(value);
  };

  // handle accordion items change
  const handleAccoidionChange = (items: MSAFilterTerm[]) => {
    setSelectedItems(
      items.map((item) => {
        if (item?.originalLabel) {
          return {
            label: item.originalLabel,
            value: item.originalValue,
            checked: true,
          };
        }

        return { ...item, checked: true };
      })
    );
  };

  // handle selected item change
  const handleSelectedChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;

    const newList = selectedItems.map((item) => {
      if (item.value === name) {
        return { ...item, checked };
      }
      return item;
    });

    setSelectedItems(newList);
  };

  // handle highlighted item change
  const handleHighlightedItemChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, checked } = e.target;

    const item = filteredMSACodes.find((item) => item.originalValue === name);

    if (checked) {
      setSelectedItems([
        ...selectedItems,
        { label: item.originalLabel, value: item.originalValue, checked },
      ]);

      return;
    }

    setSelectedItems(selectedItems.filter((item) => item.value !== name));
  };

  const handleApplyFilter = () => {
    // filter out unchecked items
    const filteredList = selectedItems.filter((item) => item.checked);

    const msas = filteredList.map((item) => ({
      value: item.value,
      label: item.label,
    }));

    setFilter(FilterKeys.LOCATION, {
      ...location,
      msas,
    });

    props.setOpen(false);
  };

  return (
    <Dialog open={props.open} onOpenChange={props.setOpen}>
      <DialogContent
        className={styles["msa-dialog-root"]}
        hasBackdrop
        lockScroll
      >
        <div className={styles["msa-dialog-body"]}>
          <div className={styles["header"]}>
            <DialogHeading className={styles["heading"]}>
              Metropolitan statistical area
            </DialogHeading>
            <IconButton
              type="button"
              Icon={<Icon.Close aria-hidden="true" />}
              size="sm"
              onClick={() => props.setOpen(false)}
              tabIndex={-1}
              aria-label="Close dialog"
            />
          </div>

          <div className={styles["msa-content"]}>
            <p className={styles["desc"]}>
              A metropolitan statistical area (MSA) is a geographic region that
              includes a central urban area and surrounding counties with strong
              economic ties. The list of MSAs available to search here are from
              a Census.gov list published July 2023 that can be{" "}
              <a
                href="https://www.census.gov/geographies/reference-files/time-series/demo/metro-micro/delineation-files.html"
                target="_blank"
                rel="noreferrer"
                aria-label="Visit Census Delineation Files"
              >
                found here
              </a>
              .
            </p>

            <div className={styles["search-wrapper"]}>
              <SearchInput
                id="msa-search"
                name="msa-search"
                placeholder="Type to search for a Metropolitan statistical area by name or code"
                inputSize="small"
                value={searchTerm}
                onChange={handleSearch}
                handleClear={() => setSearchTerm("")}
              />
            </div>

            <div className={styles["list-content"]} tabIndex={0}>
              {/* highlighted list */}
              {isSearchState && (
                <ul className={styles["highlighted-list"]}>
                  {filteredMSACodes.map((item, idx) => (
                    <li
                      key={`${item.originalValue}-${idx}`}
                      className={styles["item"]}
                    >
                      <Checkbox
                        id={item.originalValue}
                        name={item.originalValue}
                        label={`<div><span>${item.value}</span><span>${item.label}</span></div>`}
                        onChange={handleHighlightedItemChange}
                        isInnerHtml
                        checked={selecttedMSAValues.includes(
                          item.originalValue
                        )}
                      />
                    </li>
                  ))}
                </ul>
              )}

              {/* selected list */}
              {!isSearchState && selecttedMSAValues.length > 0 && (
                <>
                  <p className={styles["selected-label"]}>SELECTED</p>
                  <ul className={cn(styles["selected-list"], styles["mb-8"])}>
                    {selectedItems.map((item) => (
                      <li key={item.value} className={styles["item"]}>
                        <Checkbox
                          id={item.value}
                          name={item.value}
                          label={`<div><span>${item.value}</span><span>${item.label}</span></div>`}
                          onChange={handleSelectedChange}
                          isInnerHtml
                          checked={item.checked}
                        />
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {!isSearchState &&
                groupedMSA.map((item, i) => (
                  <MSAAccordion
                    key={i}
                    data={item}
                    open={item.state === openAccordoing}
                    // filter out unchecked items
                    selectedItems={selectedItems.filter(
                      (selectedItem) => selectedItem.checked
                    )}
                    handleAccordionOpen={handleAccordionOpen}
                    handleChange={handleAccoidionChange}
                  />
                ))}
            </div>
          </div>
        </div>

        <div className={styles["msa-dialog-footer"]}>
          <DialogClose
            className={styles["btn-close"]}
            aria-label="Close dialog"
          >
            Cancel and close
          </DialogClose>

          <Button
            type="button"
            className={styles["btn-submit"]}
            aria-label="Apply msa filter"
            disabled={isDisabled}
            onClick={handleApplyFilter}
          >
            Apply filter(s)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function groupedMSAByState(msaTerms: MSAFilterTerm[]): GroupedMSAFilterTerm[] {
  const grouped = msaTerms.reduce((acc, msa) => {
    if (!acc[msa.stateName]) {
      acc[msa.stateName] = [];
    }
    acc[msa.stateName].push({
      ...msa,
      // format msa label
      label: `<div><span style="font-weight: 400;">${msa.value}</span><span>${msa.label}</span></div>`,
    });
    return acc;
  }, {} as Record<string, MSAFilterTerm[]>);

  const groupedMSA = Object.entries(grouped).map(([state, msas]) => ({
    state,
    msas: msas.sort((a, b) => a.originalLabel.localeCompare(b.originalLabel)),
  }));

  const sort = groupedMSA.sort((a, b) => a.state.localeCompare(b.state));

  return sort;
}
